import {
  ComponentSelectOptions,
  inputCellRenderer,
  ItemSelectedHandler,
  LookupCellFunction,
  selectCellRenderer,
  TCellRenderer,
  ValidateCellFunction,
} from "@syntensor/common/components/table";
import {
  fetchComponents,
  fetchValidateSmiles,
} from "@syntensor/common/data/fetch_data";
import { getCachedData } from "@syntensor/common/data/cache";

export type TableConfig = {
  cols: TableCellConfig[];
};

export type TableCellConfig = {
  id: string;
  templateDescription?: string;
  name: string;
  width: string;
  placeholder?: string;
  inputCellRenderer?: TCellRenderer;
  itemSelected?: ItemSelectedHandler;
  validateHandler?: ValidateCellFunction;
  lookupHandler?: LookupCellFunction;
};

const levenshteinDistance = (s: string, t: string) => {
  const arr = [];
  for (let i = 0; i <= t.length; i++) {
    arr[i] = [i];
    for (let j = 1; j <= s.length; j++) {
      arr[i][j] =
        i === 0
          ? j
          : Math.min(
              arr[i - 1][j] + 1,
              arr[i][j - 1] + 1,
              arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1)
            );
    }
  }
  return arr[t.length][s.length] / s.length;
};

export const validEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const potencyCompoundConfig: TableConfig = {
  cols: [
    {
      id: "name",
      name: "Compound Name",
      templateDescription: "aspirin",
      width: "15%",
      placeholder: "Compound name",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum[col!.id!] = suggestion.name;
        datum[selectCellDataId!] = null;

        // GEFI

        if (suggestion.c.xrefs) {
          datum["smiles"] = suggestion.c.xrefs.smiles ?? "";
          datum["invalidsmiles"] = datum["smiles"].length === 0;
        }

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        return datum[this.id!].length < 4;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!].substring(0, 4).trim();
          if (searchStr.length < 4) {
            return datum;
          }

          const inputCacheKey = `fetchCompounds${searchStr}`;
          const components = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              component_type: "compound",
            })
          );

          const invalidSmilesId = `invalidsmiles`;
          const invalidNameId = `invalidname`;
          const selectCellDataId = `selectCellDataname`;

          // For genes and compounds, I think that might make sense. For a disease, somebody might query “lung cancer”
          // and want to see results like “non-small cell lung cancer” or “small cell lung cancer”. For pathways, the substring
          // might also be in the middle of the component name.

          const searchResults = components
            .filter(
              (c: ComponentSelectOptions) =>
                levenshteinDistance(
                  c.display_name.toLowerCase(),
                  datum[this.id!].toLowerCase()
                ) < 4
            )
            .map((c: ComponentSelectOptions) => ({
              c: c,
              name: c.display_name,
            }));

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;

          datum[invalidNameId] =
            components.length === 0 && datum[invalidSmilesId];
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },

    {
      id: "smiles",
      name: "SMILES",
      templateDescription: "CC(=O)OC1=CC=CC=C1C(=O)O",
      width: "15%",
      inputCellRenderer: inputCellRenderer,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        const input = datum[this.id!];
        if (input.length < 5) {
          return true;
        }

        try {
          const validationResp = await fetchValidateSmiles([input]);
          return !validationResp[0];
        } catch (err) {
          console.error(err);
        }
        return false;
      },
    },
  ],
};

export const potencyGeneConfig: TableConfig = {
  cols: [
    {
      id: "name",
      name: "Gene symbol",
      templateDescription: "A2M",
      width: "15%",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum["id"] = suggestion.c?.syntensor_id;
        datum[col!.id!] = suggestion.name;
        datum[selectCellDataId!] = null;

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        // TSC1,AKT1
        // MAP3K5

        const gene = datum[this.id].trim();
        if (gene.length < 2) {
          return true;
        }

        const hits = await getCachedData(`fetchGeneName${gene}`, () =>
          fetchComponents({
            search_string: gene,
            component_type: "gene",
          })
        );

        if (
          hits.filter((hit: any) => hit.display_name.indexOf(gene) === 0)
            .length === 0
        ) {
          return true;
        }

        return false;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!].substring(0, 2).trim();
          if (searchStr.length < 2) {
            return datum;
          }

          const inputCacheKey = `fetchGeneName${searchStr}`;
          const selectCellDataId = `selectCellDataname`;

          const genes = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              component_type: "gene",
            })
          );

          const searchResults = genes
            .filter(
              (c: ComponentSelectOptions) =>
                levenshteinDistance(
                  c.display_name.toLowerCase(),
                  datum[this.id!].toLowerCase()
                ) < 4
            )
            .map((c: ComponentSelectOptions) => ({
              c: c,
              name: c.display_name,
            }));

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },
  ],
};

export const potencyPathwayConfig: TableConfig = {
  cols: [
    {
      id: "name",
      name: "Reactome pathway name",
      templateDescription: "Macroautophagy",
      width: "15%",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum["id"] = suggestion.c?.syntensor_id;
        datum[col!.id!] = suggestion.name;
        datum[selectCellDataId!] = null;

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        const pathway = datum[this.id].trim();
        if (pathway.length < 2) {
          return true;
        }

        const hits = await getCachedData(`fetchPathwayName${pathway}`, () =>
          fetchComponents({
            search_string: pathway,
            component_type: "pathway",
          })
        );

        if (
          hits.filter((hit: any) => hit.display_name.indexOf(pathway) === 0)
            .length === 0
        ) {
          return true;
        }

        return false;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!].substring(0, 2).trim();
          if (searchStr.length < 2) {
            return datum;
          }

          const inputCacheKey = `fetchPathway${searchStr}`;
          const selectCellDataId = `selectCellDataname`;

          const pathways = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              component_type: "pathway",
            })
          );

          const searchResults = pathways
            .filter(
              (c: ComponentSelectOptions) =>
                levenshteinDistance(
                  c.display_name.toLowerCase(),
                  datum[this.id!].toLowerCase()
                ) < 4
            )
            .map((c: ComponentSelectOptions) => ({
              c: c,
              name: c.display_name,
            }));

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },
  ],
};

export const popCompoundConfig: TableConfig = {
  cols: [
    {
      id: "name",
      name: "Compound Name",
      templateDescription: "aspirin",
      width: "15%",
      placeholder: "Compound name",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum[col!.id!] = suggestion.name;
        datum[selectCellDataId!] = null;

        if (suggestion.c.xrefs) {
          datum["smiles"] = suggestion.c.xrefs.smiles ?? "";
          datum["invalidsmiles"] = datum["smiles"].length === 0;

          datum["moa"] = suggestion.c.xrefs.moa ?? "";
          datum["invalidmoa"] = datum["moa"].length === 0;

          datum["genes"] = JSON.parse(
            suggestion.c.xrefs.geneSymbolList ?? `""`
          ).join(",");
          datum["invalidgenes"] = datum["genes"].length === 0;
        }

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        return datum[this.id!].length < 4;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!].substring(0, 4).trim();
          if (searchStr.length < 4) {
            return datum;
          }

          const inputCacheKey = `fetchCompounds${searchStr}`;
          const components = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              component_type: "compound",
            })
          );

          // const invalidSmilesId = `invalidsmiles`;
          // const invalidNameId = `invalidname`;
          const selectCellDataId = `selectCellDataname`;

          // For genes and compounds, I think that might make sense. For a disease, somebody might query “lung cancer”
          // and want to see results like “non-small cell lung cancer” or “small cell lung cancer”. For pathways, the substring
          // might also be in the middle of the component name.

          const searchResults = components
            .filter(
              (c: ComponentSelectOptions) =>
                levenshteinDistance(
                  c.display_name.toLowerCase(),
                  datum[this.id!].toLowerCase()
                ) < 4
            )
            .map((c: ComponentSelectOptions) => ({
              c: c,
              name: c.display_name,
            }));

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;

          // datum[invalidNameId] =
          //   components.length === 0 && datum[invalidSmilesId];
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },

    {
      id: "smiles",
      name: "SMILES",
      templateDescription: "CC(=O)OC1=CC=CC=C1C(=O)O",
      width: "15%",
      inputCellRenderer: inputCellRenderer,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        const input = datum[this.id!];
        if (input.length < 5) {
          return true;
        }

        try {
          const validationResp = await fetchValidateSmiles([input]);
          return !validationResp[0];
        } catch (err) {
          console.error(err);
        }
        return false;
      },
    },

    {
      id: "moa",
      name: "Mechanism of Action",
      templateDescription: "Cyclooxygenase inhibitor",
      width: "15%",
      inputCellRenderer: inputCellRenderer,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        return datum[this.id!].length === 0;
      },
    },

    {
      id: "genes",
      name: "Gene Symbol List",
      templateDescription: "PTGS1,PTGS2",
      width: "15%",
      inputCellRenderer: inputCellRenderer,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        // TSC1,AKT1

        let result = false;
        for (const gene of datum[this.id].split(",")) {
          if (gene.length < 4) {
            result = true;
            break;
          }

          const hits = await getCachedData(`fetchGene${gene}`, () =>
            fetchComponents({
              search_string: gene,
              component_type: "gene",
            })
          );

          if (
            hits.filter((hit: any) => hit.display_name.indexOf(gene) === 0)
              .length === 0
          ) {
            result = true;
            break;
          }
        }
        return result;
      },
    },

    {
      id: "disease_name",
      name: "Indication Name",
      templateDescription: "arthritis",
      width: "15%",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum[col!.id!] = suggestion.name;
        datum[selectCellDataId!] = null;

        if (suggestion.c) {
          datum["disease_id"] = suggestion.c.syntensor_id ?? "";
          datum["invaliddisease_id"] = datum["disease_id"].length === 0;
        }

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        return datum[this.id!].length < 4;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!]
            .substring(0, 5)
            .toLowerCase()
            .trim();
          if (searchStr.length < 4) {
            return datum;
          }

          const inputCacheKey = `fetchDiseaseName${searchStr}`;
          const selectCellDataId = `selectCellDatadisease_name`;

          const diseases = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              component_type: "disease",
            })
          );

          const searchResults = diseases
            .map((c: ComponentSelectOptions) => {
              const dist = levenshteinDistance(
                c.display_name.toLowerCase(),
                datum[this.id!].trim().toLowerCase()
              );
              return {
                c,
                name: c.display_name,
                distance: dist,
              };
            })
            .filter((result: Record<string, any>) => result.distance < 0.85)
            .sort(
              (a: Record<string, any>, b: Record<string, any>) =>
                a.distance - b.distance
            );

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },

    {
      id: "disease_id",
      name: "EFO Indication Identifier",
      templateDescription: "EFO_0005856",
      width: "15%",
      inputCellRenderer: selectCellRenderer,
      itemSelected: (async (
        suggestion,
        datum,
        selectCellDataId,
        col,
        setTableData,
        i
      ): Promise<void> => {
        datum[selectCellDataId!] = null;

        if (suggestion.c) {
          datum[col!.id!] = suggestion.c.syntensor_id;
          datum["disease_name"] = suggestion.c.display_name ?? "";
          datum["invaliddisease_name"] = datum["disease_name"].length === 0;
        }

        setTableData!((prevData) => {
          const newData = [...prevData];
          newData[i!] = datum;
          return newData;
        });
      }) as ItemSelectedHandler,
      validateHandler: async function (
        datum: Record<string, any>
      ): Promise<boolean> {
        return datum[this.id!].length < 4;
      },
      lookupHandler: async function (
        datum: Record<string, any>
      ): Promise<Record<string, any>> {
        try {
          const searchStr = datum[this.id!].substring(0, 4).trim();
          if (searchStr.length < 4) {
            return datum;
          }

          const inputCacheKey = `fetchDiseaseId${searchStr}`;
          const selectCellDataId = `selectCellDatadisease_id`;

          // /{data_version}/components/id?search_string=EFO_000306&search_type=syntensor_id&exact=false

          const data = await getCachedData<any>(inputCacheKey, () =>
            fetchComponents({
              search_string: searchStr,
              search_type: "syntensor_id",
              exact: false,
            })
          );

          const searchResults = data
            .filter(
              (c: ComponentSelectOptions) =>
                c.syntensor_id!.indexOf(datum[this.id!]) > -1
            )
            .map((c: ComponentSelectOptions) => ({
              c: c,
              name: c.syntensor_id,
            }));

          datum[selectCellDataId] =
            searchResults.length > 0 ? searchResults : null;
        } catch (err) {
          console.error(err);
        }

        return datum;
      },
    },

    {
      id: "nctid",
      name: "NCT ID",
      templateDescription: "NCT05413421",
      width: "15%",
      placeholder: "NCT05413421",
      inputCellRenderer: inputCellRenderer,
    },
  ],
};
